// AUDITUI.A2 (#2587) — the AuditTrail component SHELL.
//
// Presentational only: it takes events and a status, and renders loading,
// error, empty or a list of rows. It fetches nothing. The host decides scope
// and does the fetching — A3's drawer against
// GET /entities/:entityType/:entityId/audit, A5's page against its own read —
// so this component never learns about routes or cursors.
//
// Skeleton and empty-state idioms follow DashboardZone3Activity.tsx (read,
// not edited — it is live with real users and cursor-paginated).
//
// DELIBERATELY NOT HERE, restricted scope for this PR:
//   - renderer SELECTION (blocked on the committed action frequency list)
//   - bespoke per-action renderers
//   - field-level diff rendering
//   - day grouping
//   - any copy about completeness, retention or immutability: there is no
//     configured retention, and append-only is false on main today
//     (admin.router.ts and platform-admin.router.ts delete audit rows), so
//     this component states nothing in either direction.

import AuditEventRow from './AuditEventRow';
import type { AuditEvent, AuditRendererRegistry } from './auditShared';

interface Props {
  events:    readonly AuditEvent[];
  loading?:  boolean;
  /** Message to show instead of the list. Presence alone means "errored". */
  error?:    string | null;
  /**
   * Injected registry (AC6, hard constraint 2). Left undefined, rows use the
   * shipped registry, which is empty by D2. Tests pass their own Map rather
   * than mutating shared module state.
   */
  registry?: AuditRendererRegistry;
  /** Injectable clock for deterministic relative times. */
  now?:      number;
}

const MESSAGE_STYLE = {
  padding:    '24px 0',
  textAlign:  'center' as const,
  color:      'var(--text-secondary)',
  fontSize:   13,
  fontFamily: 'var(--font-body)',
};

export default function AuditTrail({ events, loading = false, error = null, registry, now }: Props) {
  if (loading) {
    return (
      <div
        data-testid="audit-trail-skeleton"
        className="animate-pulse"
        aria-hidden="true"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ height: 34, borderRadius: 4, background: 'var(--border)' }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="audit-trail-error" role="alert" style={MESSAGE_STYLE}>
        {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div data-testid="audit-trail-empty" style={MESSAGE_STYLE}>
        No recorded activity for this item.
      </div>
    );
  }

  return (
    <ul
      data-testid="audit-trail-list"
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {events.map((event) => (
        <AuditEventRow key={event.id} event={event} registry={registry} now={now} />
      ))}
    </ul>
  );
}
